import React from 'react';

import { Badge, Panel, PanelBody, PanelHeader, PanelTitle } from '../../../components/ui';
import { cn } from '../../../shared/lib/cn';
import { BVCitation } from '../types';
import { formatDateLabel } from '../utils';

interface CitationPanelProps {
  citations: BVCitation[];
  title?: string;
  scrollable?: boolean;
  maxHeightClassName?: string;
  className?: string;
  bodyClassName?: string;
  scrollRegionClassName?: string;
}

export function CitationPanel({
  citations,
  title = 'Source reviews',
  scrollable = false,
  maxHeightClassName = 'max-h-[720px]',
  className,
  bodyClassName,
  scrollRegionClassName,
}: CitationPanelProps) {
  const listClassName = scrollable
    ? cn('min-h-0 flex-1 space-y-4 overflow-y-auto pr-2', maxHeightClassName, scrollRegionClassName)
    : 'space-y-4';

  return (
    <Panel className={cn(scrollable && 'overflow-hidden', className)}>
      <PanelHeader>
        <PanelTitle>{title}</PanelTitle>
      </PanelHeader>
      <PanelBody className={bodyClassName}>
        {citations.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No source reviews are available yet for this draft.
          </p>
        ) : (
          <div
            className={listClassName}
            data-testid={scrollable ? 'bv-source-reviews-scroll-region' : undefined}
          >
            {citations.map((citation, index) => (
              <div
                id={`bv-citation-${index + 1}`}
                key={`${citation.chunk_id}-${index}`}
                className="rounded-xl border border-gray-200 p-4 dark:border-dark-border"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">[{index + 1}]</Badge>
                  {citation.brand ? <Badge variant="outline">{citation.brand}</Badge> : null}
                  {citation.retailer ? <Badge variant="outline">{citation.retailer}</Badge> : null}
                  {citation.rating ? <Badge variant="outline">{citation.rating.toFixed(1)} stars</Badge> : null}
                </div>

                <p className="mt-3 text-sm leading-6 text-charcoal dark:text-gray-100">
                  {citation.review_text_snippet}
                </p>

                <div className="mt-3 grid gap-2 text-xs text-gray-500 dark:text-gray-400 sm:grid-cols-2">
                  <p>Product: {citation.product_name || 'Unknown'}</p>
                  <p>Review date: {formatDateLabel(citation.review_date)}</p>
                  <p>Reviewer: {citation.username || 'Anonymous'}</p>
                  <p>Source note: Review {index + 1}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </PanelBody>
    </Panel>
  );
}

export default CitationPanel;
